/**
 * QueueBadge — global "N orders in queue" indicator shown in the client header.
 * Polls every 30 seconds. Color-codes by load (green <5, amber 5-15, red >15).
 */
import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QueueBadge({ token, count: countProp, managed = false, className = '' }) {
  const { t } = useTranslation('common');
  const [count, setCount] = useState(null);

  // When the parent owns the number (unified /api/badges/summary), we
  // render it and never poll — not even before the first payload lands.
  const polled = !managed && (countProp === undefined || countProp === null);

  useEffect(() => {
    if (!polled) return undefined;
    let stop = false;
    const fetchIt = async () => {
      try {
        const r = await fetch(`${API_URL}/api/service/queue-count`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok || stop) return;
        const j = await r.json();
        setCount(j.count ?? null);
      } catch (_) { /* silent */ }
    };
    fetchIt();
    const iv = setInterval(fetchIt, 120000);
    return () => { stop = true; clearInterval(iv); };
  }, [token, polled]);

  const value = polled ? count : countProp;
  if (value == null) return null;
  const tone = value <= 5 ? 'from-emerald-500 to-green-600' :
               value <= 15 ? 'from-amber-500 to-orange-500' :
                             'from-rose-500 to-red-600';
  return (
    <div
      data-testid="queue-badge"
      title={t('queue_badge.tooltip', { count: value, defaultValue: `${value} orders currently being processed or waiting` })}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-white bg-gradient-to-r ${tone} shadow-sm ${className}`}
    >
      <Clock className="w-3.5 h-3.5" />
      <span className="text-xs font-bold whitespace-nowrap">
        {t('queue_badge.label', { defaultValue: 'Queue' })}: <span data-testid="queue-badge-count">{value}</span>
      </span>
    </div>
  );
}
